// Phase 11 — central copy handler.
//
// Every copy action in the app flows through `copyText`. This is the single
// hook point we'll wire telemetry to in Phase 20 ("Popular" data), so
// components never call `navigator.clipboard.writeText` directly.

export type CopySource =
  | "symbol-tile"
  | "symbol-detail"
  | "symbol-format"
  | "symbol-variant"
  | "tray-item"
  | "tray-all"
  | "copy-as"
  | "generator-line"
  | "decorative-combo"
  | "lenny-face"
  | "kaomoji"
  | "use-case-example"
  | "pack-item"
  | "pack-all"
  | "pack-link"
  | "export-pattern";

export interface CopyMeta {
  /** Where in the UI the copy originated — drives later analytics buckets. */
  source: CopySource;
  /** The raw symbol string when applicable (may differ from the value copied). */
  symbol?: string;
  /** Which format was copied for "copy as" / formats menus. */
  formatKey?: string;
}

type Listener = (meta: CopyMeta, value: string) => void;
const listeners = new Set<Listener>();

/** Subscribe to every successful copy — Phase 20 will plug analytics in here. */
export function onCopy(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Copy `value` to the OS clipboard. Returns true on success, false if the
 * browser refused (no Clipboard API, denied permission, non-secure context).
 * Never throws — UI components rely on the boolean to flash "Copied!".
 */
export async function copyText(value: string, meta: CopyMeta): Promise<boolean> {
  if (typeof navigator === "undefined") return false;
  try {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
      await navigator.clipboard.writeText(value);
    } else {
      // Legacy fallback — works in older browsers and inside some iframes.
      const ta = document.createElement("textarea");
      ta.value = value;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      if (!ok) return false;
    }
    for (const l of listeners) {
      try { l(meta, value); } catch { /* listener must never break copy */ }
    }
    return true;
  } catch {
    return false;
  }
}
